import { FINANCE_ACCOUNTS_FUNDS_URLS } from "@/features/finance/model/accounts-funds-tabs"
import {
  getActiveUserBranchAccesses,
} from "@/features/auth/lib/workspace-user-access";
import {
  resolveWorkspaceAccessRecord,
  type WorkspaceAccessRecord,
} from "@/features/auth/lib/workspace-access-record"
import {
  type AuthMeResponse,
  BRANCH_ACCESS_MODULES,
  WorkspaceAccessModule,
  WorkspaceAccessPermission,
} from "@/features/auth/model/auth-types";
import { createAppAbility } from "@/features/auth/casl/create-app-ability"
import {
  resolveTenantAuthorizationDecision,
  resolveTenantAuthorizationModuleAction,
} from "@/features/auth/tenant-authorization/lib/tenant-authorization-registry"
import {
  TenantAuthorizationAction,
  TenantAuthorizationDecisionStatus,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types"
import { normalizePathname, toCanonicalAdminPath } from "@/shared/lib/branch/branch-admin-routing";
import type { Capability } from "@ibada/shared-types/feature-flags";

export { resolveWorkspaceAccessRecord }

export type ChurchNavIconKey =
  | "home"
  | "users"
  | "wallet"
  | "chartColumn"
  | "pieChart"
  | "package"
  | "calendarDays"
  | "heartHandshake"
  | "clipboardList"
  | "settings"
  | "branch"
  | "building"

export type ChurchNavMenuGroup = "navigation" | "administration"
export type ChurchNavRouteMatch = "exact" | "prefix"

/*
 * The nav badge model moved to `@ibada/ui` with `NavMain`, so the tenant and admin sidebars share one.
 * Aliased rather than re-declared: two structurally identical enums are still two types to TypeScript, and
 * the duplicate is exactly what made the shared component reject the tenant's own badges.
 */
import {
  SidebarNavBadgeVariant as ChurchNavBadgeVariant,
  getSidebarNavBadgeLabel as getChurchNavBadgeLabel,
  type SidebarNavBadge as ChurchNavBadge,
} from "@ibada/ui/components/sidebar-nav"

export { ChurchNavBadgeVariant, getChurchNavBadgeLabel }
export type { ChurchNavBadge }

export type ChurchNavPermission =
  | {
      module: WorkspaceAccessModule
      minAccess?: WorkspaceAccessPermission
    }
  | {
      anyOf: WorkspaceAccessModule[]
      minAccess?: WorkspaceAccessPermission
    }

export type ChurchNavSubItem = {
  titleKey: string
  url: string
  enabled: boolean
  match?: ChurchNavRouteMatch
  /**
   * Extra paths this ONE entry owns, for a menu item that fronts more than one route — Accounts / Funds is a
   * single link opening a two-tab page whose tabs kept their original URLs. Without this the entry would go
   * unhighlighted on every path but `url`, so the sidebar would show nothing selected while the user is
   * plainly inside that section. `url` stays the destination; these only affect active state.
   */
  matchUrls?: readonly string[]
  badge?: ChurchNavBadge
  /** Renders the item in the accent colour so a call-to-action (e.g. resume setup) stands out from siblings. */
  highlight?: boolean
  /** Hide this item in SINGLE_BRANCH / mono workspaces. */
  requiresMultiBranchWorkspace?: boolean
  permission?: ChurchNavPermission
  mobileMenuGroup?: ChurchNavMenuGroup
  /**
   * Feature-flag capability gating this ITEM. A paused feature can live inside a shipped section — Pledges is a
   * child of Finance — so the gate has to exist at item level too, or the only way to hide it would be to hide
   * its whole parent.
   */
  capability?: Capability
}

/**
 * @deprecated The overview page no longer requires module-level permissions.
 * Any authenticated workspace member may access it; individual sections within
 * the overview carry their own access state from the backend.
 * Kept for reference in supplemental route entries that share the same historical gate.
 */
export const DASHBOARD_OVERVIEW_REQUIRED_PERMISSION: ChurchNavPermission = {
  anyOf: [...BRANCH_ACCESS_MODULES, WorkspaceAccessModule.SETTINGS],
  minAccess: WorkspaceAccessPermission.VIEW,
};

export type ChurchNavSection = {
  titleKey: string
  url: string
  primaryUrl?: string
  iconKey: ChurchNavIconKey
  match?: ChurchNavRouteMatch
  permission?: ChurchNavPermission
  mobilePrimaryTab?: boolean
  mobileMenuGroup?: ChurchNavMenuGroup
  /** Optional short label shown next to the section title (e.g. "Beta"). */
  badge?: ChurchNavBadge
  /** Feature-flag capability gating this section. When set and not available, the section is hidden and its routes are guarded. */
  capability?: Capability
  /** When false, hidden from the top header row + mobile primary tabs; home remains via logo / sidebar. Default true. */
  showInHeaderRow?: boolean
  /**
   * When true, the section is visible only to a user who can manage the CURRENT branch — its Branch ADMIN or
   * the workspace OWNER. Used by "My Branch", whose gate is branch-authority rather than a module permission
   * (which {@link ChurchNavPermission} cannot express). Evaluated in {@link filterVisibleChurchNavSections},
   * so both the desktop sidebar and the mobile nav inherit it.
   */
  requiresBranchAdmin?: boolean
  /** Hide this section in SINGLE_BRANCH / mono workspaces. */
  requiresMultiBranchWorkspace?: boolean
  items: ChurchNavSubItem[]
}

/**
 * Stable onboarding-tour anchors keyed by a section's canonical url (locale- and layout-independent).
 * The same anchor is rendered on the desktop top tab AND the mobile bottom tab, so the tour highlights
 * whichever copy is actually visible. Permission gating is inherited: a section the user can't see isn't
 * rendered, so its anchor is absent and the tour step is skipped.
 */
export const NAV_TOUR_IDS: Record<string, string> = {
  "/members": "nav-members",
  "/finance": "nav-finance",
  "/budgets": "nav-budgets",
  "/activities": "nav-activities",
  "/departments": "nav-departments",
  "/forms": "nav-forms",
  "/reports": "nav-reports",
  "/assets": "nav-assets",
  "/work-queue": "nav-work-queue",
}

export const CHURCH_NAV_ITEMS: ChurchNavSection[] = [
  {
    titleKey: "churchNav.sections.dashboard.title",
    url: "/",
    iconKey: "home",
    match: "exact",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    items: [
      {
        titleKey: "churchNav.sections.dashboard.overview",
        url: "/",
        enabled: true,
        match: "exact",
        mobileMenuGroup: "navigation",
      },
      {
        titleKey: "churchNav.sections.dashboard.auditLog",
        url: "/audit-log",
        enabled: true,
        mobileMenuGroup: "navigation",
      },
    ],
  },
  {
    titleKey: "churchNav.sections.members.title",
    url: "/members",
    primaryUrl: "/members/list",
    iconKey: "users",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    permission: {
      module: WorkspaceAccessModule.MEMBERS,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [
      {
        titleKey: "churchNav.sections.members.overview",
        url: "/members",
        enabled: true,
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.MEMBERS,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        titleKey: "churchNav.sections.members.list",
        url: "/members/list",
        enabled: true,
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.MEMBERS,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        titleKey: "churchNav.sections.members.groups",
        url: "/members/groups",
        enabled: true,
        mobileMenuGroup: "navigation",
        permission: {
          // This screen queries the Groups module; member-directory access by
          // itself must never expose a destination the API will reject.
          module: WorkspaceAccessModule.GROUPS,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        titleKey: "churchNav.sections.members.ministries",
        url: "/members/ministries",
        enabled: true,
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.MINISTRIES,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        titleKey: "churchNav.sections.members.baptism",
        url: "/members/baptism",
        enabled: true,
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.MEMBERS,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
    ],
  },
  {
    // The operational Activities surface (ADR-0017): schedule, occurrences, programs.
    titleKey: "churchNav.sections.activities.title",
    url: "/activities",
    iconKey: "calendarDays",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    permission: {
      module: WorkspaceAccessModule.ACTIVITIES,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [],
  },
  {
    titleKey: "churchNav.sections.departments.title",
    url: "/departments",
    iconKey: "package",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    permission: {
      module: WorkspaceAccessModule.DEPARTMENTS,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [],
  },
  {
    titleKey: "churchNav.sections.finance.title",
    url: "/finance",
    iconKey: "wallet",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    permission: {
      module: WorkspaceAccessModule.FINANCE,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [
      {
        titleKey: "churchNav.sections.finance.overview",
        url: "/finance",
        enabled: true,
        match: "exact",
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        titleKey: "churchNav.sections.finance.transactions",
        url: "/finance/transactions",
        enabled: true,
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        titleKey: "churchNav.sections.finance.transfers",
        url: "/finance/transfers",
        enabled: true,
        match: "prefix",
        requiresMultiBranchWorkspace: true,
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        // IBA-161. Sits next to Transfers because both are review queues, and both carry a pending count.
        // Not under Settings: reconciliation is recurring operational work, not configuration.
        titleKey: "churchNav.sections.finance.reconciliation",
        url: "/finance/reconciliation",
        enabled: true,
        match: "prefix",
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        // ONE entry for both. Accounts (where money is) and Funds (what it is earmarked for) answer the same
        // question, and Finance had grown too many top-level links for each to keep its own. They open a
        // two-tab page; each tab keeps its own URL, hence `matchUrls`.
        titleKey: "churchNav.sections.finance.accountsFunds",
        url: "/finance/accounts",
        matchUrls: FINANCE_ACCOUNTS_FUNDS_URLS,
        enabled: true,
        match: "prefix",
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        titleKey: "churchNav.sections.finance.pledges",
        url: "/finance/pledges",
        enabled: true,
        match: "prefix",
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        // Finance Investigations (IBA-75): financial traceability + the investigation workspace.
        titleKey: "churchNav.sections.finance.investigations",
        url: "/finance/investigations",
        enabled: true,
        match: "prefix",
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
      {
        // Settings: categories, accounts, fiscal periods, opening balances, reconciliation.
        titleKey: "churchNav.sections.finance.settings",
        url: "/finance/settings",
        enabled: true,
        match: "prefix",
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FINANCE,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
    ],
  },
  {
    /*
     * Reports — a platform capability, not a Finance page, and no longer feature-flagged.
     *
     * The `reports.view` capability gated the old report BUILDER, which is retired. What lives at this URL
     * now is the predefined-template catalog every module contributes to, so the real gate is the REPORTS
     * module permission below. The planner separately requires read access to each report's own entity
     * (ADR-0013 D2), so this section appearing never implies every report inside it will run.
     */
    titleKey: "churchNav.sections.reports.title",
    url: "/reports",
    iconKey: "chartColumn",
    // No badge here any more. "New" is now an operator decision in Admin -> Features, carried by a
    // seeded row, so retiring it after the release is a toggle rather than a deploy.
    mobilePrimaryTab: false,
    mobileMenuGroup: "navigation",
    permission: {
      module: WorkspaceAccessModule.REPORTS,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [],
  },
  {
    titleKey: "churchNav.sections.budgets.title",
    url: "/budgets",
    iconKey: "pieChart",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    // Budgets are paused, but never public. The shared Coming Soon page is still a budgeting destination:
    // it remains visible only to users with budgets.view and its route guard enforces the same decision.
    permission: {
      module: WorkspaceAccessModule.BUDGETING,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [],
  },
  {
    titleKey: "churchNav.sections.assets.title",
    url: "/assets",
    primaryUrl: "/assets/items",
    capability: "assets.view",
    iconKey: "package",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    permission: {
      module: WorkspaceAccessModule.ASSETS,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    // Items / categories / history are sibling views of one table, so they live in the toolbar
    // segmented control (like members) rather than as sidebar children.
    items: [],
  },
  {
    titleKey: "churchNav.sections.forms.title",
    url: "/forms",
    iconKey: "clipboardList",
    capability: "forms.view",
    mobilePrimaryTab: true,
    mobileMenuGroup: "navigation",
    // Forms is a standalone branch-scoped module permission (FORMS), enforced on the backend via CASL — so the
    // nav must gate on it directly. A user with FORMS=NONE must not see the Forms tab even with other access.
    permission: {
      module: WorkspaceAccessModule.FORMS,
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [
      {
        titleKey: "churchNav.sections.forms.overview",
        url: "/forms",
        enabled: true,
        mobileMenuGroup: "navigation",
        permission: {
          module: WorkspaceAccessModule.FORMS,
          minAccess: WorkspaceAccessPermission.VIEW,
        },
      },
    ],
  },
  {
    // Branch self-service ("My Branch"): Overview + Team, visible only to whoever can manage the current
    // branch (its Branch ADMIN or the workspace OWNER). Reuses the existing branch details + team containers.
    titleKey: "churchNav.sections.myBranch.title",
    url: "/my-branch",
    iconKey: "branch",
    mobilePrimaryTab: false,
    mobileMenuGroup: "administration",
    showInHeaderRow: false,
    requiresBranchAdmin: true,
    items: [
      {
        titleKey: "churchNav.sections.myBranch.overview",
        url: "/my-branch",
        enabled: true,
        match: "exact",
        mobileMenuGroup: "administration",
      },
      {
        titleKey: "churchNav.sections.myBranch.team",
        url: "/my-branch/team",
        enabled: true,
        mobileMenuGroup: "administration",
      },
    ],
  },
  {
    titleKey: "churchNav.sections.settings.title",
    url: "/settings",
    iconKey: "settings",
    mobilePrimaryTab: false,
    mobileMenuGroup: "administration",
    showInHeaderRow: false,
    permission: {
      anyOf: [
        WorkspaceAccessModule.SETTINGS,
        WorkspaceAccessModule.GROUPS,
        WorkspaceAccessModule.MINISTRIES,
        WorkspaceAccessModule.FINANCE,
        WorkspaceAccessModule.BUDGETING,
      ],
      minAccess: WorkspaceAccessPermission.VIEW,
    },
    items: [],
  },
]

/** Routes not listed in {@link CHURCH_NAV_ITEMS} but still scoped by workspace module access. */
const ADMIN_MODULE_ROUTE_SUPPLEMENT: Array<{
  path: string
  match: ChurchNavRouteMatch
  permission: ChurchNavPermission | null
}> = [
  { path: "/people", match: "prefix", permission: { module: WorkspaceAccessModule.MEMBERS, minAccess: WorkspaceAccessPermission.VIEW } },
  { path: "/programmes", match: "prefix", permission: { module: WorkspaceAccessModule.ACTIVITIES, minAccess: WorkspaceAccessPermission.VIEW } },
  { path: "/settings/system/roles", match: "prefix", permission: { module: WorkspaceAccessModule.SETTINGS, minAccess: WorkspaceAccessPermission.VIEW } },
  { path: "/profile", match: "prefix", permission: null },
]

export type ChurchNavAccessContext = {
  authMe: AuthMeResponse | null | undefined
  activeWorkspaceSlug?: string | null
  /** Branch id for the current admin URL segment (from UserBranchAccess); drives module CASL scope. */
  activeWorkspaceBranchId?: string | null
  /** True for multi-branch workspaces. False hides nav entries reserved for branch-to-branch workflows. */
  showMultiBranchWorkspaceUi?: boolean
  /**
   * Section urls to keep visible regardless of module permission. Used for ownership-based access that the
   * declarative module gate can't express (e.g. a department-responsible user who has no DEPARTMENTS permission
   * but must still reach `/departments`). Only single-link sections (no sub-items) should be forced.
   */
  forceVisibleUrls?: ReadonlySet<string>
}

export function isSystemBranchContext(
  access: WorkspaceAccessRecord | null,
  urlBranchId: string | null | undefined,
): boolean {
  if (!access || !urlBranchId) return false
  return getActiveUserBranchAccesses(access).some(
    (entry) => entry.branchId === urlBranchId && entry.branch?.branchType === "SYSTEM",
  )
}

function resolveModuleForSelectedBranchType(
  module: WorkspaceAccessModule,
  selectedBranchIsSystem: boolean,
): WorkspaceAccessModule | null {
  if (!selectedBranchIsSystem) return module

  // In the parish (SYSTEM branch) context, operational modules keep their real identity — access is the
  // real module evaluated on the SYSTEM branch. Only MEMBERS has no parish-wide surface, so it is hidden.
  if (module === WorkspaceAccessModule.MEMBERS) return null
  return module
}

export function resolvePermissionForSelectedBranchType(
  permission: ChurchNavPermission | null | undefined,
  access: WorkspaceAccessRecord | null,
  urlBranchId: string | null | undefined,
): { available: boolean; permission: ChurchNavPermission | null | undefined } {
  if (!permission) return { available: true, permission }

  const selectedBranchIsSystem = isSystemBranchContext(access, urlBranchId)
  if (!selectedBranchIsSystem) return { available: true, permission }

  if ("anyOf" in permission) {
    const mapped = permission.anyOf
      .map((module) => resolveModuleForSelectedBranchType(module, true))
      .filter((module): module is WorkspaceAccessModule => Boolean(module))
    if (mapped.length === 0) return { available: false, permission: null }
    return { available: true, permission: { ...permission, anyOf: mapped } }
  }

  const mappedModule = resolveModuleForSelectedBranchType(permission.module, true)
  if (!mappedModule) return { available: false, permission: null }
  return { available: true, permission: { ...permission, module: mappedModule } }
}

export function isAdminPathUnavailableInSelectedBranchType(
  canonicalAdminPath: string,
  access: WorkspaceAccessRecord | null,
  urlBranchId: string | null | undefined,
): boolean {
  if (!isSystemBranchContext(access, urlBranchId)) return false
  const path = normalizePathname(canonicalAdminPath)
  return (
    path === "/members" ||
    path.startsWith("/members/") ||
    path === "/people" ||
    path.startsWith("/people/")
  )
}

export function hasRequiredModuleAccess(
  access: WorkspaceAccessRecord | null,
  permission?: ChurchNavPermission,
  urlBranchId?: string | null,
): boolean {
  if (!permission) return true
  if (!access) return false

  const branchPermission = resolvePermissionForSelectedBranchType(permission, access, urlBranchId)
  if (!branchPermission.available) return false
  if (!branchPermission.permission) return true

  const ability = createAppAbility(access)
  const requestedBranchSlug = isSystemBranchContext(access, urlBranchId)
    ? "parish"
    : getActiveUserBranchAccesses(access).find((entry) => entry.branchId === urlBranchId)?.branch?.slug
  const requiredAccess = branchPermission.permission.minAccess ?? WorkspaceAccessPermission.VIEW
  const hasModuleAccess = (module: WorkspaceAccessModule) =>
    resolveTenantAuthorizationDecision(
      { access, ability, requestedBranchSlug },
      resolveTenantAuthorizationModuleAction(module, requiredAccess),
    ).status === TenantAuthorizationDecisionStatus.Allowed
  return "anyOf" in branchPermission.permission
    ? branchPermission.permission.anyOf.some(hasModuleAccess)
    : hasModuleAccess(branchPermission.permission.module)
}

export function hasAnyGrantedModuleAccess(access: WorkspaceAccessRecord | null): boolean {
  if (!access) return false
  return access.roles.some(
    (role) => role.roleStatus !== "INACTIVE" && (role.permissions?.length ?? 0) > 0,
  )
}

/**
 * Audit-log navigation and route guards share the typed registry action. No caller can substitute generic
 * dashboard or settings access for this decision.
 */
export function canAccessAuditLog(access: WorkspaceAccessRecord | null): boolean {
  if (!access) return false
  return resolveTenantAuthorizationDecision(
    { access, ability: createAppAbility(access) },
    TenantAuthorizationAction.AuditLogView,
  ).status === TenantAuthorizationDecisionStatus.Allowed
}

function pathFromNavUrl(url: string): string {
  // Nav URLs are already context-relative admin paths (`/members`, `/finance/transactions`); just strip the
  // query and normalize. (They must NOT go through toCanonicalAdminPath, which is for full tenant pathnames
  // and would treat the first segment as a workspace slug in the flat URL model.)
  const withoutQuery = url.split("?")[0] ?? url
  return normalizePathname(withoutQuery)
}

function buildAdminModuleMatchRules(): Array<{
  path: string
  match: ChurchNavRouteMatch
  permission: ChurchNavPermission | null
}> {
  const collected: Array<{
    path: string
    match: ChurchNavRouteMatch
    permission: ChurchNavPermission | null
  }> = []

  const addRule = (path: string, match: ChurchNavRouteMatch, permission: ChurchNavPermission | null | undefined) => {
    collected.push({ path, match, permission: permission ?? null })
  }

  for (const section of CHURCH_NAV_ITEMS) {
    addRule(pathFromNavUrl(section.url), section.match ?? "prefix", section.permission)
    for (const item of section.items) {
      if (!item.enabled) continue
      addRule(
        pathFromNavUrl(item.url),
        item.match ?? section.match ?? "prefix",
        item.permission ?? section.permission,
      )
    }
  }

  for (const entry of ADMIN_MODULE_ROUTE_SUPPLEMENT) {
    addRule(entry.path, entry.match, entry.permission)
  }

  collected.sort((a, b) => b.path.length - a.path.length)
  return collected
}

let cachedAdminModuleMatchRules: ReturnType<typeof buildAdminModuleMatchRules> | null = null

/**
 * Longest-prefix match against nav + supplemental admin routes.
 * Returns `null` when no module is required (e.g. profile only).
 *
 * Module-denied redirects avoid looping by sending users without required access to a fallback such as `/profile`.
 */
export function getRequiredModulePermissionForCanonicalAdminPath(canonicalPath: string): ChurchNavPermission | null {
  const path = normalizePathname(canonicalPath)

  const rules = cachedAdminModuleMatchRules ?? (cachedAdminModuleMatchRules = buildAdminModuleMatchRules())

  for (const rule of rules) {
    const isActive =
      rule.match === "exact"
        ? path === rule.path
        : path === rule.path || path.startsWith(`${rule.path}/`)
    if (!isActive) continue
    return rule.permission
  }
  return null
}

export function getRequiredModulePermissionForPathname(fullPathname: string): ChurchNavPermission | null {
  return getRequiredModulePermissionForCanonicalAdminPath(toCanonicalAdminPath(fullPathname))
}

export function filterVisibleChurchNavSections(
  items: ChurchNavSection[],
  context: ChurchNavAccessContext,
): ChurchNavSection[] {
  const access = resolveWorkspaceAccessRecord(context.authMe, context.activeWorkspaceSlug)
  const branchId = context.activeWorkspaceBranchId ?? null
  const ability = createAppAbility(access)
  const requestedBranchSlug = isSystemBranchContext(access, branchId)
    ? "parish"
    : getActiveUserBranchAccesses(access).find((entry) => entry.branchId === branchId)?.branch?.slug
  const canManageCurrentBranch =
    resolveTenantAuthorizationDecision(
      { access, ability, requestedBranchSlug },
      TenantAuthorizationAction.UsersView,
    ).status === TenantAuthorizationDecisionStatus.Allowed
  const isPermissionAllowed = (permission: ChurchNavPermission | null | undefined) => {
    if (!permission) return true
    const allowsModule = (module: WorkspaceAccessModule) =>
      resolveTenantAuthorizationDecision(
        { access, ability, requestedBranchSlug },
        resolveTenantAuthorizationModuleAction(module, permission.minAccess ?? WorkspaceAccessPermission.VIEW),
      ).status === TenantAuthorizationDecisionStatus.Allowed

    return "anyOf" in permission
      ? permission.anyOf.some(allowsModule)
      : allowsModule(permission.module)
  }

  return items.flatMap((section) => {
    // "My Branch" and similar branch-authority sections: gate on managing the current branch (Branch ADMIN
    // or workspace OWNER), which the declarative module gate can't express.
    if (section.requiresBranchAdmin && !canManageCurrentBranch) {
      return []
    }
    if (section.requiresMultiBranchWorkspace && context.showMultiBranchWorkspaceUi === false) {
      return []
    }
    // Ownership-based override: keep a single-link section visible even without the module permission.
    if (context.forceVisibleUrls?.has(section.url) && section.items.length === 0) {
      return [{ ...section, items: [] }]
    }
    const visibleItems = section.items.filter((item) => {
      if (!item.enabled) return false
      if (item.requiresMultiBranchWorkspace && context.showMultiBranchWorkspaceUi === false) return false
      const permissionForBranch = resolvePermissionForSelectedBranchType(item.permission ?? section.permission, access, branchId)
      if (!permissionForBranch.available) return false
      if (item.url === "/audit-log") {
        if (!canAccessAuditLog(access)) return false
      } else if (!isPermissionAllowed(permissionForBranch.permission)) {
        return false
      }
      return true
    })

    if (section.items.length > 0) {
      if (visibleItems.length === 0) return []
      return [{ ...section, items: visibleItems }]
    }

    const sectionPermission = resolvePermissionForSelectedBranchType(section.permission, access, branchId)
    if (!sectionPermission.available) return []
    if (!isPermissionAllowed(sectionPermission.permission)) return []

    return [{ ...section, items: [] }]
  })
}

/** Horizontal header tabs + mobile primary strip: omit sections with `showInHeaderRow: false` (e.g. dashboard — same permission as other branch modules). */
export function churchNavSectionsForCompactHeader(sections: ChurchNavSection[]): ChurchNavSection[] {
  return sections.filter((section) => section.showInHeaderRow !== false)
}

export type ResolveFirstAccessibleAdminLandingPathOptions = {
  /**
   * Skip dashboard home and audit log when picking a destination.
   * Used when workspace overview / activity APIs return 403 so we do not bounce back to `/`.
   */
  excludeDashboardOverview?: boolean
  /**
   * Prefer the same landing as the compact header’s first tab ({@link churchNavSectionsForCompactHeader}), then
   * fall back to skipping `/` (overview) only, then any allowed route.
   */
  preferNonDashboardOverview?: boolean
}

function isDashboardOverviewAdminPath(canonicalAdminPath: string): boolean {
  if (canonicalAdminPath === "/") return true
  if (canonicalAdminPath === "/audit-log" || canonicalAdminPath.startsWith("/audit-log/")) return true
  return canonicalAdminPath === "/recent-activity" || canonicalAdminPath.startsWith("/recent-activity/")
}

function pickAccessibleAdminLandingPath(
  visible: ChurchNavSection[],
  excludeDashboardOverview: boolean,
): string | null {
  const shouldSkip = (canonicalAdminPath: string) =>
    excludeDashboardOverview && isDashboardOverviewAdminPath(canonicalAdminPath)

  for (const section of visible) {
    if (section.items.length > 0) {
      for (const item of section.items) {
        const rawPath = pathFromNavUrl(item.url)
        const canonical = normalizePathname(rawPath)
        if (shouldSkip(canonical)) continue
        return rawPath
      }
    } else {
      const rawPath = pathFromNavUrl(section.url)
      const canonical = normalizePathname(toCanonicalAdminPath(rawPath))
      if (shouldSkip(canonical)) continue
      return rawPath
    }
  }

  return null
}

/** First church-nav destination the user may open (canonical paths like `/members`). */
export function resolveFirstAccessibleAdminLandingPath(
  context: ChurchNavAccessContext,
  options?: ResolveFirstAccessibleAdminLandingPathOptions,
): string | null {
  const excludeOnly = options?.excludeDashboardOverview ?? false
  const preferNonDashboard = options?.preferNonDashboardOverview ?? false

  const visible = filterVisibleChurchNavSections(CHURCH_NAV_ITEMS, context)

  if (preferNonDashboard) {
    const compactVisible = churchNavSectionsForCompactHeader(visible)
    const fromCompactRow = pickAccessibleAdminLandingPath(compactVisible, false)
    if (fromCompactRow) return fromCompactRow
    const nonDashboard = pickAccessibleAdminLandingPath(visible, true)
    if (nonDashboard) return nonDashboard
    return pickAccessibleAdminLandingPath(visible, false)
  }

  return pickAccessibleAdminLandingPath(visible, excludeOnly)
}

/**
 * Post-login / shell “home” path: first tab in {@link churchNavSectionsForCompactHeader} the user may open.
 * Same rule set as routing — {@link hasRequiredModuleAccess} delegates to {@link createAppAbility} (CASL).
 */
export function resolvePreferredTenantAdminLandingPath(
  context: ChurchNavAccessContext,
): string | null {
  return resolveFirstAccessibleAdminLandingPath(context, { preferNonDashboardOverview: true })
}

export function isChurchNavPathActive(
  pathname: string,
  targetUrl: string | readonly string[],
  match: ChurchNavRouteMatch = "prefix",
): boolean {
  if (Array.isArray(targetUrl)) {
    return (targetUrl as readonly string[]).some((url) => isChurchNavPathActive(pathname, url, match))
  }
  const currentPath = toCanonicalAdminPath(pathname)
  // Navigation targets are already canonical admin-relative paths (for example
  // `/finance/transactions`). Running them through `toCanonicalAdminPath` would
  // misread any two-segment target as the legacy `/:workspace/:branch` URL shape
  // and collapse it to `/`, making unrelated links appear active on Home.
  const targetPath = normalizePathname(targetUrl as string)
  if (match === "exact") {
    return currentPath === targetPath
  }
  if (currentPath === targetPath) return true
  return currentPath.startsWith(`${targetPath}/`)
}

export function findCurrentChurchNavSection(
  items: ChurchNavSection[],
  pathname: string,
): ChurchNavSection | null {
  const sortedSections = [...items].sort((a, b) => b.url.length - a.url.length)
  const matched = sortedSections.find((section) => {
    if (isChurchNavPathActive(pathname, section.url, section.match)) return true
    return section.items.some((item) => isChurchNavPathActive(pathname, item.matchUrls ?? item.url, item.match))
  })
  return matched ?? null
}
